<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router';
import { AltenarSportsbook } from '@/shared/SportsBook/AltenarSportsbook';
import { copyToClipboard as copyToClipboardHelper } from '@/utils/apostatudo/clipboard';

const altenarSportsbookInstance = AltenarSportsbook.getInstance();

const altenarContainer = ref<HTMLDivElement | null>(null);
const codeToCopy = ref<string>('');
const shareCodeInput = ref<HTMLInputElement | null>(null);
const isModalBottomVisible = ref<boolean>(false);

const { altenar } = useRuntimeConfig().public;
const { integration } = altenar;

const { t } = useTexts();
const { fullSportsPageUrl } = useSportsBook();
const layout = useLayoutStore();
const { ApiService } = useApiService();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { updateWallet } = useWallet();

const route = useRoute();
const { localeRoute } = useRoutes();

const shareCodeParam = useRouteQuery<string>('shareCode', '', {
  transform: getFirstRouteParam,
});

const reservationCodeParam = useRouteQuery<string>('reservationCode', '', {
  transform: getFirstRouteParam,
});

let pendingTokenRequest: Promise<string | undefined> | null = null;

function getToken(source: string): Promise<string | undefined> {
  if (!isAuthenticated.value) {
    return Promise.resolve(undefined);
  }

  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }

  const params = new URLSearchParams({
    source,
    tab_id: getTabId(),
    mounted_id: getMountId(),
  });

  pendingTokenRequest = ApiService.get(`/alternar/token?${params}`)
    .then(({ data }) => (data.token as string) || undefined)
    .finally(() => {
      pendingTokenRequest = null;
    });

  return pendingTokenRequest;
}

async function renewToken(source: string): Promise<void> {
  const token = (await getToken(source)) || '';

  altenarSportsbookInstance.set({
    token,
  });
}

function scrollToTop() {
  if (!altenarContainer.value) {
    return;
  }

  setTimeout(() => {
    altenarContainer.value?.scrollTo({ top: 0, behavior: 'smooth' });
  }, 50);
}

async function copyToClipboard() {
  try {
    shareCodeInput.value?.select();
  } catch {}

  const ok = await copyToClipboardHelper(codeToCopy.value);

  if (ok) {
    layout.openToaster({
      message: t('account.copied_link'),
      type: 'info',
    });

    return;
  }

  if (!isModalBottomVisible.value) {
    isModalBottomVisible.value = true;
  } else {
    layout.openToaster({
      message: t('casino.game.sharing.not_supported'),
      type: 'error',
    });
  }
}

async function onShareCode(data: any) {
  const betSlipLink = `${fullSportsPageUrl.value}?shareCode=${data.code}`;

  codeToCopy.value = betSlipLink;

  await copyToClipboard();
}

function onBetPlacement() {
  updateWallet();
}

let isRenewing = false;

function onUserTokenExpiration() {
  if (isRenewing) return;

  isRenewing = true;

  renewToken('onUserTokenExpiration').finally(async () => {
    await sleep(5000);

    isRenewing = false;
  });
}

function onSignInButtonClick() {
  openLogin();
}

function onRouterLoad() {
  altenarSportsbookInstance.executeQueue();
}

function onRouteChange() {
  scrollToTop();
}

function onRouteChangeDetailed(to: AltenarSportsbook.PathEntry) {
  debug('altenar: onRouteChangeDetailed', to);

  const readableUrl = altenarSportsbookInstance.getReadableUrl(to);

  if (!readableUrl) {
    return;
  }

  navigateTo(localeRoute(readableUrl));
}

async function startAltenar() {
  try {
    const token = await getToken('startAltenar');

    if (!altenarContainer.value) {
      throw new Error('altenar: container div not found');
    }

    altenarSportsbookInstance.init({
      culture: 'pt-BR',
      integration,
      token,
      container: altenarContainer.value,
      props: {
        routerType: 'memory',
        shareCode: shareCodeParam.value,
        reservationCode: reservationCodeParam.value,
        onShareCode,
        onBetPlacement,
        onSignInButtonClick,
        onRouterLoad,
        onRouteChange,
        onRouteChangeDetailed,
        onUserTokenExpiration,
      },
    });

    setAltenarPage();
  } catch (err) {
    throw new Error(`altenar: error on initializing script\n${err}`);
  }
}

function checkInstanceAndStart() {
  verifyInstanceOnWindow([
    {
      key: 'altenarWSDK',
      onLoad: async () => {
        await startAltenar();
      },
    },
  ]);
}

watch(isAuthenticated, async () => {
  await renewToken('watchIsAuthenticated');
});

watch(
  () => route.path,
  () => {
    setAltenarPage();
  },
);

function setAltenarPage() {
  const path = route.path;

  const staticRoutes: Record<string, AltenarSportsbook.PathEntry> = {
    '/sports': { page: 'overview' },
    '/sports/live': { page: 'live' },
    '/sports/my-bets': { page: 'betHistory' },
    '/sports/e-sports': { page: 'esports' },
    '/sports/virtual': { page: 'virtual' },
    '/sports/toto': { page: 'toto' },
  };

  if (staticRoutes[path]) {
    return altenarSportsbookInstance.goToPage(staticRoutes[path]);
  }

  const { prefix, id, suffix } = getDataFromUrl(path);

  if (isNaN(id)) {
    return;
  }

  const to: AltenarSportsbook.PathEntry = {};
  const numericSuffix = suffix ? Number(suffix) : undefined;

  switch (prefix) {
    // Compatibility: old championship format
    case 'c':
      to.page = 'championship';
      to.championshipIds = [id];
      break;

    case 'ch':
      to.page = 'championship';
      to.championshipIds = [id];
      break;

    // Compatibility: old sport format
    case 's':
      to.page = 'sport';
      to.sportId = id;
      if (numericSuffix) to.sportTypeId = numericSuffix;
      break;

    case 'sp':
      to.page = 'sport';
      to.sportId = id;
      if (numericSuffix) to.sportTypeId = numericSuffix;
      break;

    // Compatibility: old event format
    case 'e':
      to.page = 'event';
      to.eventId = id;
      break;

    case 'ev':
      to.page = 'event';
      to.eventId = id;
      break;

    case 'le':
      to.page = 'liveEvent';
      to.eventId = id;
      break;

    case 'ca':
      to.page = 'category';
      to.categoryIds = [id];
      break;

    case 'es':
      to.page = 'esports';
      if (suffix === 'ch') to.championshipIds = [id];
      else if (suffix === 'ca') to.categoryIds = [id];
      else if (suffix === 'sp') to.sportId = id;
      break;

    case 'co':
      to.page = 'coupon';
      to.sportId = id;
      if (numericSuffix) to.couponType = numericSuffix;
      break;

    default:
      return;
  }

  altenarSportsbookInstance.goToPage(to);
}

onMounted(() => {
  checkInstanceAndStart();
});

onActivated(() => {
  checkInstanceAndStart();
});

onBeforeRouteLeave(() => {
  altenarSportsbookInstance.kill();
});
</script>

<template>
  <div
    ref="altenarContainer"
    :class="$style.altenar"
  >
  </div>

  <BaseModalBottom
    v-model="isModalBottomVisible"
    description="Copie o código abaixo e compartilhe"
    title="Compartilhar bilhete"
  >
    <template #footer>
      <input
        ref="shareCodeInput"
        :class="$style.shareCodeInput"
        readonly
        type="text"
        :value="codeToCopy"
      />

      <button
        :class="$style.shareCodeButton"
        @click="copyToClipboard()"
      >
        <NuxtIcon name="copy" />
        Copiar código
      </button>
    </template>
  </BaseModalBottom>
</template>

<style module lang="postcss">
.altenar {
  @apply w-full overflow-auto p-2;
}

.shareCodeInput {
  @apply select-all w-full my-4 flex flex-wrap border-none rounded-md relative bg-auth-bg-inputs text-auth-text-inputs;
}

.shareCodeButton {
  @apply w-full flex justify-center items-center gap-2 p-3 font-semibold text-primary;
}
</style>
